/**
 * app/AutoActualizar.tsx — SAFE_AUTO_UPDATE_V2: la caja se actualiza sola y una versión obligatoria no
 * deja empezar operaciones nuevas.
 *
 *  - La versión nueva espera (registerType 'prompt', sin skipWaiting automático). Esta pestaña le pide
 *    activarse («CELMAXX_ACTIVAR») sólo sin operación; el service worker cuenta las pestañas reales del POS
 *    (public/sw-celmaxx.js) y sólo se activa si hay una. Con más, se pide cerrar las otras.
 *  - Versión: al montar, cada 5 min, al volver la red y al volver a la pestaña se lee /version.json (sin
 *    caché). Si mi build es anterior a `minimoObligatorio`, la pantalla «Actualización obligatoria» aparece
 *    EN EL INSTANTE en que no hay operación abierta (se escuchan el carrito, los tickets en espera, las
 *    ventanas y los formularios al cambiar, no por muestreo); nunca encima de una, y una vez puesta no deja
 *    pasar teclas ni el escáner a la caja. Sin conexión no se bloquea.
 *  - Se recarga sólo cuando la versión nueva toma el control tras un pedido de ESTA pestaña, y sólo sin
 *    operación ni formulario tecleado: no hay recargas en bucle.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useCartStore } from '@features/pos/store/cartStore'
import { useHeldStore } from '@features/pos/store/heldStore'
import { db } from '@lib/dexie/schema'
import {
  esObligatoria,
  gateVisible,
  hayVentanaAbierta,
  hayVersionNueva,
  momentoSeguro,
  operacionAbierta,
  siguientePaso,
  sinOperacion,
  type EstadoCaja,
  type VersionPublicada,
} from './momentoSeguro'
import { pagina } from './pagina'

const REVISAR_CADA_MS = 5 * 60_000
const VIGILAR_CADA_MS = 15_000
const OTRAS_PESTANAS = 'Hay otras pestañas de CELMAXX POS abiertas. Ciérralas para actualizar.'
const GATE = '[data-celmaxx-gate]'
const CERRAR =
  'Cierra esta pestaña de CELMAXX POS y vuelve a abrirla para terminar la actualización.'

// Una sola instancia por pestaña (se monta una vez en App).
const caja = {
  registro: null as ServiceWorkerRegistration | null,
  // Se resuelve con el registro (o null sin service worker / si falló): las decisiones lo esperan.
  registroListo: Promise.resolve(null) as Promise<ServiceWorkerRegistration | null>,
  servidor: null as VersionPublicada | null, // última versión publicada leída
  revisadoPara: null as string | null, // gitSha publicado contra el que ya se revisó el service worker
  ultimaActividad: Date.now(),
  pedido: false, // esta pestaña pidió la activación
  // Formulario tecleado fuera de la caja: se marca al teclear y se limpia al cambiar de pantalla.
  rutaSucia: null as string | null,
}

/** Lo que se ve ahora mismo, sin esperar a nadie (decide la pantalla obligatoria). */
function instantanea(): EstadoCaja {
  const ruta = window.location.pathname
  if (caja.rutaSucia !== null && caja.rutaSucia !== ruta) caja.rutaSucia = null
  return {
    carrito: useCartStore.getState().items.length,
    enEspera: useHeldStore.getState().tickets.length,
    hayVentana: hayVentanaAbierta(),
    formularioSucio: caja.rutaSucia !== null,
    pendientesSync: 0,
    ruta,
    inactivoMs: Date.now() - caja.ultimaActividad,
    oculta: document.visibilityState === 'hidden',
  }
}

/** Instantánea + escrituras pendientes de sincronizar (decide activar y recargar). */
async function estado(): Promise<EstadoCaja> {
  let pendientesSync = 0
  try {
    pendientesSync =
      (await db.outbox.where('status').anyOf(['pending', 'syncing']).count()) +
      (await db.offline_sale_intents.where('status').anyOf(['draft', 'queued', 'syncing']).count())
  } catch {
    pendientesSync = 1 // si no se puede leer, se asume que hay algo por sincronizar
  }
  return { ...instantanea(), pendientesSync }
}

/** Pide al service worker en espera que se active; él decide según las pestañas reales. */
function pedirActivacion(): Promise<{ activado: boolean; clientes: number } | null> {
  const enEspera = caja.registro?.waiting
  if (!enEspera) return Promise.resolve(null)
  return new Promise(resolve => {
    const canal = new MessageChannel()
    const limite = setTimeout(() => resolve(null), 5_000)
    canal.port1.onmessage = ev => {
      clearTimeout(limite)
      resolve(ev.data as { activado: boolean; clientes: number })
    }
    caja.pedido = true
    enEspera.postMessage({ type: 'CELMAXX_ACTIVAR' }, [canal.port2])
  })
}

async function leerVersion(): Promise<VersionPublicada | null> {
  try {
    const r = await fetch('/version.json', { cache: 'no-store' })
    return r.ok ? ((await r.json()) as VersionPublicada) : null
  } catch {
    return null
  }
}

/** Una sola recarga por versión publicada (por pestaña): si tras recargar seguimos viejos, no hay bucle. */
function yaRecargo(sha: string): boolean {
  try {
    return sessionStorage.getItem('celmaxx-recarga:' + sha) === '1'
  } catch {
    return true // sin sessionStorage no se arriesga un bucle
  }
}
function marcarRecarga(sha: string) {
  try {
    sessionStorage.setItem('celmaxx-recarga:' + sha, '1')
  } catch {
    /* yaRecargo ya devuelve true sin sessionStorage */
  }
}

/** Pide al navegador revisar el service worker contra la versión publicada y lo deja anotado. */
async function revisarServiceWorker(v: VersionPublicada) {
  const reg = await caja.registroListo
  if (!reg) {
    caja.revisadoPara = v.gitSha // sin service worker la recarga trae lo nuevo de la red
    return
  }
  try {
    await reg.update()
    caja.revisadoPara = v.gitSha
  } catch {
    /* sin red: no se da por revisado; se reintenta en la siguiente comprobación */
  }
}

/** Da el siguiente paso de la actualización (ver siguientePaso) y devuelve el aviso para la pantalla. */
const ESPERA = 'Termina lo que está abierto y se instala sola.'

/**
 * ¿Se puede actuar YA? Tras la parte asíncrona (outbox en Dexie) se repite la parte síncrona de la vista
 * (carrito, tickets, ventanas, formulario) sin ninguna espera entre esta comprobación y la acción: una
 * operación que empezó mientras se esperaba a la red o a Dexie impide recargar o activar (Codex r1).
 */
async function sigueLibre(condicion: (s: EstadoCaja) => boolean): Promise<boolean> {
  const s = await estado()
  return condicion(s) && condicion({ ...instantanea(), pendientesSync: s.pendientesSync })
}

/** Recarga sólo si la caja sigue sin operación en este mismo instante. */
async function recargarSiLibre(): Promise<boolean> {
  if (!(await sigueLibre(sinOperacion))) return false
  pagina.recargar()
  return true
}

/**
 * Da el siguiente paso de la actualización (ver siguientePaso) y devuelve el aviso para la pantalla.
 * `condicion`: cuándo se puede actuar (botón ⇒ sin operación; automático ⇒ el criterio del vigilante); se
 * re-evalúa justo antes de activar o recargar, después de toda espera.
 */
async function avanzar(condicion: (s: EstadoCaja) => boolean): Promise<string | null> {
  const reg = await caja.registroListo
  const sha = caja.servidor?.gitSha ?? ''
  const paso = siguientePaso({
    enEspera: !!reg?.waiting,
    bajando: !!reg?.installing,
    hayNueva: hayVersionNueva(__APP_BUILD__, caja.servidor),
    revisado: caja.revisadoPara === sha,
    yaRecargo: yaRecargo(sha),
  })
  if (paso === 'activar') {
    if (!(await sigueLibre(condicion))) return ESPERA
    const r = await pedirActivacion() // envía el pedido en este mismo turno, sin esperas antes
    return r?.activado
      ? 'Actualizando…'
      : r && r.clientes > 1
        ? OTRAS_PESTANAS
        : 'No se pudo actualizar; reintenta.'
  }
  if (paso === 'bajando') return 'Descargando la versión nueva…'
  if (paso === 'recargar') {
    if (!(await sigueLibre(condicion))) return ESPERA
    marcarRecarga(sha)
    pagina.recargar()
    return 'Actualizando…'
  }
  return paso === 'cerrar' ? CERRAR : null
}

const mismaVista = (a: EstadoCaja, b: EstadoCaja) =>
  a.carrito === b.carrito &&
  a.enEspera === b.enEspera &&
  a.hayVentana === b.hayVentana &&
  a.formularioSucio === b.formularioSucio

export function AutoActualizar() {
  const [pendiente, setPendiente] = useState(false)
  const [obligatoria, setObligatoria] = useState(false)
  const [enLinea, setEnLinea] = useState(navigator.onLine)
  const [vista, setVista] = useState<EstadoCaja>(instantanea)
  const [nota, setNota] = useState<string | null>(null)
  const [fijo, setFijo] = useState(false) // la pantalla obligatoria, una vez puesta, se queda
  const recargar = useRef(false)
  const ref = useRef({ pendiente, obligatoria })
  ref.current = { pendiente, obligatoria }

  const mostrarGate = obligatoria && enLinea && (fijo || gateVisible(vista, obligatoria, enLinea))
  useEffect(() => {
    if (mostrarGate) setFijo(true)
  }, [mostrarGate])

  // Reacción inmediata: carrito, tickets en espera, ventanas (DOM) y formularios tecleados.
  useEffect(() => {
    const refrescar = () => {
      const s = instantanea()
      setVista(prev => (mismaVista(prev, s) ? prev : s))
    }
    let programado = false
    const alCambiarDom = () => {
      if (programado) return
      programado = true
      queueMicrotask(() => {
        programado = false
        refrescar()
      })
    }
    const alTeclear = (ev: Event) => {
      const t = ev.target as Element | null
      if (!t || t.closest(GATE)) return
      // En la caja lo que importa ya lo cubren el carrito, los tickets en espera y las ventanas.
      if (window.location.pathname === '/pos') return
      caja.rutaSucia = window.location.pathname
      refrescar()
    }
    const quitarCarrito = useCartStore.subscribe(refrescar)
    const quitarEspera = useHeldStore.subscribe(refrescar)
    const observador = new MutationObserver(alCambiarDom)
    observador.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    })
    document.addEventListener('input', alTeclear, true)
    document.addEventListener('change', alTeclear, true)
    window.addEventListener('popstate', refrescar)
    return () => {
      quitarCarrito()
      quitarEspera()
      observador.disconnect()
      document.removeEventListener('input', alTeclear, true)
      document.removeEventListener('change', alTeclear, true)
      window.removeEventListener('popstate', refrescar)
    }
  }, [])

  // Con la pantalla obligatoria puesta no se puede empezar NADA en la caja de abajo:
  //  - la app entera (#root) queda `inert`: sin clics, sin foco (Tab no sale de la pantalla), aunque una capa
  //    de la app tenga un z-index mayor; la pantalla vive fuera de #root (portal) para seguir activa;
  //  - ninguna tecla viaja a los atajos de la caja (F2…F9, escáner): se cortan en captura en window, antes
  //    de cualquier escucha. Desde la propia pantalla no se cancela la acción por defecto, así Enter/espacio
  //    siguen pulsando ACTUALIZAR AHORA (el clic es otro evento); desde fuera se cancela también.
  useEffect(() => {
    if (!mostrarGate) return
    const raiz = document.getElementById('root')
    raiz?.setAttribute('inert', '')
    const cortar = (ev: KeyboardEvent) => {
      if (!(ev.target as Element | null)?.closest?.(GATE)) ev.preventDefault()
      ev.stopImmediatePropagation()
    }
    const teclas = ['keydown', 'keypress', 'keyup'] as const
    for (const t of teclas) window.addEventListener(t, cortar, true)
    return () => {
      raiz?.removeAttribute('inert')
      for (const t of teclas) window.removeEventListener(t, cortar, true)
    }
  }, [mostrarGate])

  useEffect(() => {
    // Primero el registro: las decisiones (revisar, activar, recargar) lo esperan.
    const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null
    caja.registroListo = sw
      ? sw.register('/sw.js', { scope: '/' }).then(
          reg => {
            caja.registro = reg
            if (reg.waiting && sw.controller) setPendiente(true)
            reg.addEventListener('updatefound', () => {
              const nuevo = reg.installing
              nuevo?.addEventListener('statechange', () => {
                if (nuevo.state === 'installed' && sw.controller) setPendiente(true)
              })
            })
            return reg
          },
          () => null
        )
      : Promise.resolve(null)

    const comprobarVersion = async () => {
      const v = await leerVersion()
      if (!v) return // sin red o sin respuesta: no se decide nada nuevo (no bloquea por falso negativo)
      caja.servidor = v
      setObligatoria(esObligatoria(__APP_BUILD__, v))
      if (hayVersionNueva(__APP_BUILD__, v) && caja.revisadoPara !== v.gitSha)
        await revisarServiceWorker(v)
    }
    void comprobarVersion()
    const revisar = setInterval(() => void comprobarVersion(), REVISAR_CADA_MS)

    const marcar = () => {
      caja.ultimaActividad = Date.now()
    }
    const alConectar = () => {
      setEnLinea(true)
      void comprobarVersion()
    }
    const alDesconectar = () => setEnLinea(false)
    const alVolver = () => {
      if (document.visibilityState === 'visible') void comprobarVersion()
    }
    window.addEventListener('pointerdown', marcar)
    window.addEventListener('keydown', marcar)
    window.addEventListener('online', alConectar)
    window.addEventListener('offline', alDesconectar)
    document.addEventListener('visibilitychange', alVolver)

    // Tomó el control una versión nueva: la pedimos nosotros, o el navegador la activó solo (p. ej. con la
    // pestaña sin controlar) y la publicada es otra que la mía. Primera instalación de la misma versión: nada.
    const alTomarControl = () => {
      if (!caja.pedido && !hayVersionNueva(__APP_BUILD__, caja.servidor)) return
      recargar.current = true
      void recargarSiLibre()
    }
    sw?.addEventListener('controllerchange', alTomarControl)

    const vigilar = setInterval(() => {
      void estado().then(async s => {
        if (recargar.current) {
          void recargarSiLibre()
          return
        }
        const { pendiente, obligatoria } = ref.current
        const criterio = obligatoria ? sinOperacion : momentoSeguro
        if (!criterio(s)) return
        if (!pendiente && !obligatoria && !hayVersionNueva(__APP_BUILD__, caja.servidor)) return
        setNota(await avanzar(criterio))
      })
    }, VIGILAR_CADA_MS)

    return () => {
      clearInterval(vigilar)
      clearInterval(revisar)
      sw?.removeEventListener('controllerchange', alTomarControl)
      window.removeEventListener('pointerdown', marcar)
      window.removeEventListener('keydown', marcar)
      window.removeEventListener('online', alConectar)
      window.removeEventListener('offline', alDesconectar)
      document.removeEventListener('visibilitychange', alVolver)
    }
  }, [])

  const actualizarAhora = async () => {
    const s = await estado()
    if (!sinOperacion(s)) {
      setNota(
        s.formularioSucio
          ? 'Guarda lo que estás capturando y sal de esa pantalla; luego se actualiza.'
          : s.pendientesSync > 0
            ? 'Esperando a que se sincronicen las operaciones pendientes…'
            : ESPERA
      )
      return
    }
    // Revisar el service worker contra la versión publicada antes de decidir (no recargar a ciegas).
    if (caja.servidor && caja.revisadoPara !== caja.servidor.gitSha)
      await revisarServiceWorker(caja.servidor)
    setNota(await avanzar(sinOperacion))
  }

  if (mostrarGate) {
    return createPortal(
      <div
        data-celmaxx-gate
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="celmaxx-gate-titulo"
        // Por encima de cualquier capa de la app (hay capas hasta z-[10000]).
        style={{ zIndex: 2147483647 }}
        className="fixed inset-0 flex items-center justify-center bg-navy-900/95 p-4"
      >
        <div className="w-full max-w-md rounded-3xl bg-white p-6 text-center shadow-2xl">
          <p className="text-4xl">🔄</p>
          <h2 id="celmaxx-gate-titulo" className="mt-2 text-xl font-black text-navy-900">
            Actualización obligatoria
          </h2>
          <p className="mt-2 text-[14px] text-navy-600">
            Hay una nueva versión de CELMAXX POS. Actualiza para continuar.
          </p>
          {nota && <p className="mt-3 text-[13px] font-semibold text-warning-700">{nota}</p>}
          <button
            type="button"
            autoFocus
            onClick={() => void actualizarAhora()}
            className="mt-5 h-12 w-full rounded-2xl bg-brand-primary font-bold text-brand-on-primary"
          >
            ACTUALIZAR AHORA
          </button>
        </div>
      </div>,
      document.body
    )
  }

  if (!pendiente) return null
  return (
    <div
      role="status"
      className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 rounded-2xl bg-navy-900 text-white px-4 py-2.5 shadow-lg text-[13px] max-w-[calc(100vw-32px)]"
    >
      <span>
        🔄 Hay una versión nueva.{' '}
        {nota ??
          'Se instala sola cuando la caja esté libre (sin venta, ventanas, formularios ni pendientes).'}
      </span>
      {!operacionAbierta(vista) && (
        <button
          type="button"
          onClick={() => void actualizarAhora()}
          className="flex-shrink-0 rounded-xl bg-white text-navy-900 font-bold px-3 py-1.5"
        >
          Actualizar ahora
        </button>
      )}
    </div>
  )
}
